import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'

export interface RatingData {
  rating: number
  book_count: number
  percentage: number
}

export interface UseRatingDistributionReturn {
  data: RatingData[]
  averageRating: number
  totalRatedBooks: number
  isLoading: boolean
  error: Error | null
}

/**
 * Hook for rating distribution data
 * Uses PostgreSQL stored function for maximum performance
 */
export function useRatingDistribution(): UseRatingDistributionReturn {
  const { user } = useAuth()

  const { data, isLoading, error } = useQuery({
    queryKey: ['rating-distribution', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated')

      // Uses PostgreSQL stored function for optimization
      const { data, error } = await supabase.rpc('get_rating_distribution', {
        user_uuid: user.id,
      })

      if (error) {
        console.error('Rating distribution error:', error)
        throw new Error(`Failed to fetch rating distribution: ${error.message}`)
      }

      return data || []
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes - ratings don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes in cache
    retry: 2,
  })

  // Calculate aggregate statistics
  const processedData = data || []
  const averageRating =
    processedData.length > 0 && processedData[0]?.average_rating
      ? Number(processedData[0].average_rating)
      : 0
  const totalRatedBooks = processedData.reduce(
    (sum: number, item: { book_count?: number }) => sum + (item.book_count || 0),
    0
  )

  return {
    data: processedData,
    averageRating,
    totalRatedBooks,
    isLoading,
    error: error as Error | null,
  }
}
